import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/** Shown for somebody the streak view has no row for -- renders as a plain dash. */
private val EMPTY_STREAK =
    StreakPreview(levels = emptyList(), todayLevel = 0, pending = false, repaid = false)

/** A date range one of the period boards covers. */
private data class Period(val fromDate: String, val toDate: String)

/** What the board's last column reports. */
private enum class TrailingColumn(val header: String) {
    /** All-time board: the streak currently running. */
    Streak("Streak"),

    /** Period boards: how many days of that week or month were completed. */
    CompletedDays("Splněné dny"),
}

/**
 * All three boards at once -- all-time, this week and this month -- stacked under their own
 * headings (SPECIFIKACE.md ch. 6.2, ch. 8 point 4). The weekly and monthly ones can step back one
 * period, which is what makes a finished week worth looking at: while it is still running, a
 * partial week says little.
 *
 * The period boards need no view of their own: they are the daily summaries of the range, summed
 * per participant here. One fetch covers every range on screen. A row opens that participant's
 * history.
 */
@Composable
fun LeaderboardScreen(token: String, onOpenHistory: (String) -> Unit) {
    val state = rememberAsyncState()
    var participants by remember { mutableStateOf<Map<String, Participant>>(emptyMap()) }
    var allTime by remember { mutableStateOf<List<Standing>>(emptyList()) }
    var days by remember { mutableStateOf<List<DailySummary>>(emptyList()) }
    var medals by remember { mutableStateOf<Map<String, MedalTotals>>(emptyMap()) }
    var periodMedals by remember { mutableStateOf<List<PeriodMedalTotals>>(emptyList()) }
    var showPreviousWeek by remember { mutableStateOf(false) }
    var showPreviousMonth by remember { mutableStateOf(false) }

    val today = todayIso()
    val thisWeek = Period(startOfWeekIso(today), today)
    val previousWeekStart = startOfPreviousWeekIso(today)
    val previousWeek = Period(previousWeekStart, endOfWeekIso(previousWeekStart))
    val thisMonth = Period(startOfMonthIso(today), today)
    val previousMonthStart = startOfPreviousMonthIso(today)
    val previousMonth = Period(previousMonthStart, endOfMonthIso(previousMonthStart))

    LaunchedEffect(Unit) {
        state.run {
            val byId = Api.fetchParticipants(includeInactive = true).associateBy { it.id }
            participants = byId
            medals = Api.fetchMedalTotals(token).associateBy { it.participantId }
            periodMedals = Api.fetchPeriodMedalTotals(
                token,
                minOf(previousWeek.fromDate, previousMonth.fromDate),
            )

            val streaks = Api.fetchStreaks(token).associateBy { it.participantId }
            allTime = Api.fetchLeaderboard(token).mapNotNull { row ->
                byId[row.participantId]?.let {
                    Standing(it, row.totalPoints, row.rank.toInt(), streak = streaks[row.participantId])
                }
            }

            // One fetch spanning every range shown, so switching to the previous week or month is
            // instant and costs nothing extra.
            days = Api.fetchDailySummary(
                token,
                fromDate = minOf(previousWeek.fromDate, previousMonth.fromDate),
                toDate = today,
            )
        }
    }

    fun standingsIn(period: Period): List<Standing> =
        leaderboardFrom(days.filter { it.entryDate >= period.fromDate && it.entryDate <= period.toDate }, participants)

    fun medalsIn(period: Period, kind: String): Map<String, MedalTotals> = periodMedals
        .asSequence()
        .filter { it.periodKind == kind && it.startsOn == period.fromDate }
        .associate { row ->
            row.participantId to MedalTotals(row.participantId, row.gold, row.silver, row.bronze)
        }

    ErrorMessage(state.error)

    if (!state.loaded) {
        LoadingText()
        return
    }

    BoardSection(
        title = "Celkem",
        subtitle = "Od začátku evidence",
        rows = allTime,
        trailing = TrailingColumn.Streak,
        pointsHeader = "Body celkem",
        emptyText = "Zatím nikdo nemá žádné body.",
        onOpenHistory = onOpenHistory,
        medals = medals,
    )

    val week = if (showPreviousWeek) previousWeek else thisWeek
    BoardSection(
        title = "Týden",
        subtitle = formatCzechDateRange(week.fromDate, week.toDate),
        rows = standingsIn(week),
        trailing = TrailingColumn.CompletedDays,
        emptyText = if (showPreviousWeek) {
            "Minulý týden nikdo nic nezapsal."
        } else {
            "Tenhle týden zatím nikdo nemá žádné body."
        },
        onOpenHistory = onOpenHistory,
        periodSwitch = {
            NavButton("Tento týden", !showPreviousWeek) { showPreviousWeek = false }
            NavButton("Minulý týden", showPreviousWeek) { showPreviousWeek = true }
        },
        medals = if (showPreviousWeek) medalsIn(week, "week") else null,
    )

    val month = if (showPreviousMonth) previousMonth else thisMonth
    BoardSection(
        title = "Měsíc",
        subtitle = formatCzechDateRange(month.fromDate, month.toDate),
        rows = standingsIn(month),
        trailing = TrailingColumn.CompletedDays,
        emptyText = if (showPreviousMonth) {
            "Minulý měsíc nikdo nic nezapsal."
        } else {
            "Tenhle měsíc zatím nikdo nemá žádné body."
        },
        onOpenHistory = onOpenHistory,
        periodSwitch = {
            NavButton("Tento měsíc", !showPreviousMonth) { showPreviousMonth = false }
            NavButton("Minulý měsíc", showPreviousMonth) { showPreviousMonth = true }
        },
        medals = if (showPreviousMonth) medalsIn(month, "month") else null,
    )
}

@Composable
private fun BoardSection(
    title: String,
    subtitle: String,
    rows: List<Standing>,
    trailing: TrailingColumn,
    emptyText: String,
    onOpenHistory: (String) -> Unit,
    pointsHeader: String = "Body",
    periodSwitch: (@Composable () -> Unit)? = null,
    medals: Map<String, MedalTotals>? = null,
) {
    Div(attrs = { classes("board") }) {
        Div(attrs = { classes("board-head") }) {
            H3 { Text(title) }
            periodSwitch?.let { Div(attrs = { classes("tab-row", "tab-row-inline") }) { it() } }
        }
        P(attrs = { classes("board-range") }) { Text(subtitle) }

        if (rows.isEmpty()) {
            P { Text(emptyText) }
            return@Div
        }

        Table {
            Thead {
                Tr {
                    Th { Text("#") }
                    Th { Text("Jméno") }
                    Th { Text(pointsHeader) }
                    Th { Text(trailing.header) }
                    if (medals != null) Th { Text("Medaile") }
                }
            }
            Tbody {
                rows.forEach { standing ->
                    Tr(attrs = {
                        classes("clickable-row")
                        onClick { onOpenHistory(standing.participant.id) }
                    }) {
                        Td { Text(standing.rank.toString()) }
                        Td { Text(standing.participant.name) }
                        Td { Text(formatNumber(standing.points)) }
                        Td {
                            when (trailing) {
                                TrailingColumn.Streak -> StreakBadges(standing.streak?.asPreview() ?: EMPTY_STREAK)
                                TrailingColumn.CompletedDays -> Text(standing.completedDays.toString())
                            }
                        }
                        if (medals != null) Td { MedalSummary(medals[standing.participant.id]) }
                    }
                }
            }
        }
    }
}
